'use client'

import {
  createContext,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import { prefersReducedMotion } from '@/core/lib/motion'
import { LayerHeadSlot } from '@/features/veluvit/components/overlay/layer-slot'
import { EASE_OUT, gsap } from '@/features/veluvit/lib/gsap'

import type { ReactNode } from 'react'

/* 시트 안 이동 — 메뉴에서 항목을 누르면 페이지로 넘어가지 않고 같은 시트 안에서 다음 화면이 옆에서 들어온다.
   머리(SheetHead)는 이 맥락이 있으면 왼쪽에 뒤로 버튼을 단다. 넓은 화면이 필요한 항목(wide)은 PC 에서 시트가 옆으로 넓어진다.
   아래 화면들은 가려 두기만 하고 그대로 둔다 — 입력하던 값이 뒤로 갔다 와도 남는다. */

type SheetScreen = {
  key: string
  render: () => ReactNode
  /** PC 에서 시트를 넓게(760px) 쓴다 — 목록·표처럼 폭이 넉넉해야 좋은 화면 */
  wide?: boolean
}

type SheetNavApi = {
  depth: number
  push: (screen: SheetScreen) => void
  pop: () => void
}

const SheetNavContext = createContext<SheetNavApi | null>(null)

/** SheetHead 가 뒤로 버튼을 달지 정할 때 쓴다 */
export function useSheetNav() {
  return useContext(SheetNavContext)
}

/** 시트 폭 — layer-host 의 시트 틀이 이 변수로 PC 최대 폭을 정하고, 바뀔 때 부드럽게 넓어진다 */
const WIDTH = { base: '540px', wide: '760px' }

function ScreenView({
  screen,
  top,
  direction,
}: {
  screen: SheetScreen
  top: boolean
  direction: 1 | -1
}) {
  const ref = useRef<HTMLDivElement>(null)
  // 맨 위로 올라올 때마다 들어오는 방향(앞으로 = 오른쪽에서, 뒤로 = 왼쪽에서)으로 살짝 밀려 들어온다
  useLayoutEffect(() => {
    if (!top || !ref.current) return
    if (prefersReducedMotion()) return
    gsap.fromTo(
      ref.current,
      { x: 20 * direction, autoAlpha: 0 },
      {
        x: 0,
        autoAlpha: 1,
        duration: 0.24,
        ease: EASE_OUT,
        clearProps: 'transform',
      }
    )
  }, [top, direction])
  // 판 머리 칸에는 맨 위 화면의 머리만 — 가려 둔 화면의 머리는 제자리(숨김 칸 안)에 둔다
  const slot = useContext(LayerHeadSlot)
  return (
    <div ref={ref} hidden={!top} inert={!top}>
      <LayerHeadSlot.Provider value={top ? slot : null}>
        {screen.render()}
      </LayerHeadSlot.Provider>
    </div>
  )
}

export default function SheetNav({ root }: { root: SheetScreen }) {
  const [stack, setStack] = useState<SheetScreen[]>([root])
  const [direction, setDirection] = useState<1 | -1>(1)
  const anchor = useRef<HTMLDivElement>(null)
  const top = stack[stack.length - 1]

  // 시트 틀(가장 가까운 dialog)에 폭을 알려 준다
  useLayoutEffect(() => {
    const panel = anchor.current?.closest<HTMLElement>('[role="dialog"]')
    panel?.style.setProperty('--sheet-w', top.wide ? WIDTH.wide : WIDTH.base)
  }, [top])

  const api: SheetNavApi = {
    depth: stack.length - 1,
    push: (screen) => {
      setDirection(1)
      setStack((prev) => [...prev, screen])
    },
    pop: () => {
      setDirection(-1)
      setStack((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev))
    },
  }

  return (
    <SheetNavContext.Provider value={api}>
      <div ref={anchor}>
        {stack.map((screen, index) => (
          <ScreenView
            key={screen.key}
            screen={screen}
            top={index === stack.length - 1}
            direction={direction}
          />
        ))}
      </div>
    </SheetNavContext.Provider>
  )
}
